/**
 * CustomerFinancialTab - Wrapper da aba Financeiro do modal de cliente
 * Divide o conteúdo em três sub-abas:
 *   1) Cobranças      - lista atual (CustomerChargesTab)
 *   2) Recorrência    - assinaturas do cliente (CustomerSubscriptionsTab)
 *   3) Cartões salvos - tokens de cartão do cliente (SavedCardsList)
 *
 * Cada sub-aba exibe contador no título para facilitar a leitura.
 *
 * @version 1.1.0
 * RELEASE: 2026-06-10 v1.0.0 - Criação do wrapper com sub-abas
 * RELEASE: 2026-06-20 v1.1.0 - Nova sub-aba "Cartões salvos" (Fase 5/C.3)
 */
import { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Wallet, Repeat, CreditCard, History } from 'lucide-react';
import { CustomerChargesTab } from '@/components/financial/CustomerChargesTab';
import { CustomerSubscriptionsTab } from '@/components/financial/CustomerSubscriptionsTab';
import { CustomerCreditHistoryTab } from '@/components/financial/CustomerCreditHistoryTab';
import { useCustomerCreditHistory } from '@/hooks/financial/useCustomerCreditHistory';
import { SavedCardsList } from '@/components/financial/SavedCardsList';
import { useFinanceiroCobrancasPaginated } from '@/hooks/useFinanceiroCobrancasPaginated';
import { useFinanceiroAssinaturasPaginated } from '@/hooks/useFinanceiroAssinaturasPaginated';
import { useSavedCards } from '@/hooks/financial/useSavedCards';

interface CustomerFinancialTabProps {
  customerId: string;
  isActive?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

type SubTab = 'charges' | 'subscriptions' | 'credits' | 'cards';

export function CustomerFinancialTab({
  customerId,
  isActive = false,
  canEdit = true,
  canDelete = true,
}: CustomerFinancialTabProps) {
  const [subTab, setSubTab] = useState<SubTab>('charges');

  // Contadores leves (pageSize 1, só p/ totalCount) — só busca quando a aba financeiro estiver ativa
  const { totalCount: chargesCount } = useFinanceiroCobrancasPaginated(
    isActive ? { customer_id: customerId } : undefined,
    { page: 1, pageSize: 1 },
  );

  const { totalCount: subscriptionsCount } = useFinanceiroAssinaturasPaginated(
    isActive ? { customer_id: customerId } : undefined,
    { page: 1, pageSize: 1 },
  );

  // Cartões salvos: só busca quando aba financeira ativa
  const { data: savedCards } = useSavedCards(customerId, isActive);
  const cardsCount = savedCards?.length ?? 0;

  // Contador de eventos de crédito (busca leve só quando aba financeira ativa)
  const { data: creditHistory } = useCustomerCreditHistory({
    customerId,
    enabled: isActive,
    limit: 1,
  });
  const creditsCount = creditHistory?.resumo?.total_eventos ?? 0;

  return (
    <Tabs
      value={subTab}
      onValueChange={v => setSubTab(v as SubTab)}
      className="flex flex-col h-full"
    >
      <TabsList className="mx-4 mt-3 mb-2 grid w-fit grid-cols-4 h-9">
        <TabsTrigger value="charges" className="gap-2 text-xs">
          <Wallet className="h-3.5 w-3.5" />
          Cobranças
          <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-mono">
            {chargesCount}
          </Badge>
        </TabsTrigger>
        <TabsTrigger value="subscriptions" className="gap-2 text-xs">
          <Repeat className="h-3.5 w-3.5" />
          Recorrência
          <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-mono">
            {subscriptionsCount}
          </Badge>
        </TabsTrigger>
        <TabsTrigger value="credits" className="gap-2 text-xs">
          <History className="h-3.5 w-3.5" />
          Créditos
          <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-mono">
            {creditsCount}
          </Badge>
        </TabsTrigger>
        <TabsTrigger value="cards" className="gap-2 text-xs">
          <CreditCard className="h-3.5 w-3.5" />
          Cartões salvos
          <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-mono">
            {cardsCount}
          </Badge>
        </TabsTrigger>
      </TabsList>

      <TabsContent value="charges" className="flex-1 overflow-hidden mt-0">
        <CustomerChargesTab
          customerId={customerId}
          isActive={isActive && subTab === 'charges'}
          canEdit={canEdit}
          canDelete={canDelete}
        />
      </TabsContent>

      <TabsContent value="subscriptions" className="flex-1 overflow-hidden mt-0">
        <CustomerSubscriptionsTab
          customerId={customerId}
          isActive={isActive && subTab === 'subscriptions'}
          canEdit={canEdit}
          canDelete={canDelete}
        />
      </TabsContent>

      <TabsContent value="credits" className="flex-1 overflow-hidden mt-0">
        <CustomerCreditHistoryTab
          customerId={customerId}
          isActive={isActive && subTab === 'credits'}
        />
      </TabsContent>

      <TabsContent value="cards" className="flex-1 overflow-auto mt-0 px-4 pb-4">
        <SavedCardsList customerId={customerId} canRevoke={canEdit} />
      </TabsContent>
    </Tabs>
  );
}
